<template>
  <div class="scan-qr-page">
    <!-- 头部 -->
    <header class="page-header">
      <div class="header-left" @click="goBack">
        <span class="back-icon">←</span>
        <span>{{ $t('common.back') }}</span>
      </div>
      <h1 class="page-title">{{ $t('stall.scanQR') }}</h1>
      <div class="header-right"></div>
    </header>

    <!-- 扫码区域 -->
    <div class="scan-area">
      <!-- 摄像头视频 -->
      <div class="video-wrapper" v-show="scanActive">
        <video ref="video" id="qr-video" playsinline></video>
        <canvas ref="canvas" id="qr-canvas" style="display:none;"></canvas>
        <div class="scan-frame">
          <div class="scan-frame-corner tl"></div>
          <div class="scan-frame-corner tr"></div>
          <div class="scan-frame-corner bl"></div>
          <div class="scan-frame-corner br"></div>
          <div class="scan-line"></div>
        </div>
      </div>

      <!-- 未启动时显示按钮 -->
      <div v-if="!scanActive" class="scan-placeholder">
        <div class="placeholder-icon">📷</div>
        <p class="placeholder-text">{{ $t('Stall.ScanQR.line31_text') }}</p>
        <button class="start-scan-btn" @click="startScan">
          启动摄像头
        </button>
        <div class="divider">{{ $t('Stall.ScanQR.line35_text') }}</div>
        <button class="select-image-btn" @click="triggerFileSelect">
          🖼️ 从相册选择图片
        </button>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          style="display:none;"
          @change="handleImageSelect"
        />
      </div>

      <p class="scan-hint">{{ $t('scan.hint') }}</p>

      <!-- 停止按钮 -->
      <div v-if="scanActive" class="scan-controls">
        <button class="stop-scan-btn" @click="stopScan">
          ⏹ 停止扫码
        </button>
        <button class="select-image-btn-inline" @click="triggerFileSelect">
          🖼️ 从相册选择
        </button>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          style="display:none;"
          @change="handleImageSelect"
        />
      </div>
    </div>

    <!-- 手动输入 -->
    <div class="manual-input">
      <div class="input-label">{{ $t('scan.inputLabel') }}</div>
      <div class="input-box">
        <input
          v-model="packageToken"
          type="text"
          :placeholder="$t('scan.placeholder')"
          class="token-input"
        />
        <button class="submit-btn" @click="submitToken">{{ $t('scan.enter') }}</button>
      </div>
    </div>

    <!-- 扫码记录 -->
    <div class="scan-history">
      <div class="history-title">{{ $t('scan.historyTitle') }}</div>
      <div v-if="history.length === 0" class="empty-history">
        {{ $t('scan.noHistory') }}
      </div>
      <div
        v-for="item in history"
        :key="item.id"
        class="history-item"
        @click="goToPackage(item.token)"
      >
        <div class="history-info">
          <div class="history-name">{{ item.packageName }}</div>
          <div class="history-date">{{ item.date }}</div>
        </div>
        <div class="history-arrow">→</div>
      </div>
    </div>
  </div>
</template>

<script>
import jsQR from 'jsqr';

export default {
  name: 'ScanQR',
  data() {
    return {
      packageToken: '',
      history: [],
      scanActive: false,
      videoStream: null,
      scanning: false,
      animationFrameId: null
    };
  },
  created() {
    this.loadHistory();
  },
  beforeDestroy() {
    this.stopScan();
  },
  methods: {
    goBack() {
      this.stopScan();
      this.$router.back();
    },

    async startScan() {
      // 检查浏览器支持
      if (!navigator.mediaDevices) {
        if (location.protocol === 'http:' && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
          this.$toast.error('摄像头功能需要 HTTPS 连接，请使用 HTTPS 访问');
        } else {
          this.$toast.error('当前浏览器不支持摄像头访问');
        }
        return;
      }

      try {
        const video = this.$refs.video;
        const canvas = this.$refs.canvas;

        // 请求摄像头权限
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment', // 优先使用后置摄像头
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        });

        this.videoStream = stream;
        video.srcObject = stream;
        video.setAttribute('playsinline', true);
        video.play();

        this.scanActive = true;
        this.scanning = true;

        // 等待视频加载完成
        await new Promise((resolve) => {
          video.onloadedmetadata = () => {
            resolve();
          };
        });

        // 设置 canvas 尺寸
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // 开始扫码
        this.scanFrame();
      } catch (err) {
        console.error('摄像头启动失败:', err);
        if (err.name === 'NotAllowedError') {
          this.$toast.error('摄像头权限被拒绝，请在浏览器设置中允许访问摄像头');
        } else if (err.name === 'NotFoundError') {
          this.$toast.error('未找到摄像头设备');
        } else {
          this.$toast.error('摄像头启动失败：' + err.message);
        }
        this.scanActive = false;
      }
    },

    scanFrame() {
      if (!this.scanning) return;

      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      const context = canvas.getContext('2d', { willReadFrequently: true });

      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert'
        });

        if (code) {
          // 扫码成功
          this.onScanSuccess(code.data);
          return;
        }
      }

      // 继续扫描下一帧
      this.animationFrameId = requestAnimationFrame(() => this.scanFrame());
    },

    onScanSuccess(decodedText) {
      // 停止扫码
      this.scanning = false;
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
      }

      // 震动反馈
      if (navigator.vibrate) {
        navigator.vibrate(200);
      }

      console.log('扫码结果:', decodedText);

      // 提取 token
      let token = decodedText.trim();
      if (token.includes('/h5/package/')) {
        const match = token.match(/\/h5\/package\/([a-zA-Z0-9_-]+)/);
        if (match) {
          token = match[1];
        }
      }

      this.packageToken = token;
      this.goToPackage(token);
    },

    triggerFileSelect() {
      // 先停止摄像头扫描
      if (this.scanActive) {
        this.stopScan();
      }
      this.$refs.fileInput.click();
    },
    handleImageSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // 创建 canvas 并绘制图片
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          // 获取图片数据并扫码
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: 'dontInvert'
          });

          if (code) {
            this.onScanSuccess(code.data);
          } else {
            this.$toast.error('未检测到二维码，请更换图片重试');
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
      // 清空 input，允许重复选择同一文件
      event.target.value = '';
    },

    stopScan() {
      this.scanning = false;
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => track.stop());
        this.videoStream = null;
      }
      const video = this.$refs.video;
      if (video) {
        video.srcObject = null;
      }
      this.scanActive = false;
    },

    submitToken() {
      if (!this.packageToken.trim()) {
        this.$toast.error(this.$t('scan.errorEmpty'));
        return;
      }
      this.goToPackage(this.packageToken.trim());
    },

    goToPackage(token) {
      this.saveHistory(token, token);
      this.$router.push(`/h5/package/${token}`);
    },

    loadHistory() {
      const history = JSON.parse(localStorage.getItem('scanHistory') || '[]');
      this.history = history.slice(0, 10);
    },

    saveHistory(token, packageName) {
      const history = JSON.parse(localStorage.getItem('scanHistory') || '[]');
      const newItem = {
        id: Date.now(),
        token,
        packageName,
        date: new Date().toLocaleDateString()
      };
      history.unshift(newItem);
      localStorage.setItem('scanHistory', JSON.stringify(history.slice(0, 20)));
    }
  }
};
</script>

<style lang="scss" scoped>
.scan-qr-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: calc(16px + env(safe-area-inset-top, 0px));
  display: flex;
  align-items: center;
  justify-content: space-between;

  .header-left {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;

    .back-icon {
      font-size: 20px;
    }
  }

  .page-title {
    font-size: 18px;
    font-weight: 600;
  }

  .header-right {
    width: 60px;
  }
}

.scan-area {
  background: white;
  padding: 20px;
  text-align: center;

  .video-wrapper {
    position: relative;
    width: 100%;
    max-width: 350px;
    margin: 0 auto 20px;
    aspect-ratio: 1;

    #qr-video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 12px;
    }

    .scan-frame {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 200px;
      height: 200px;

      .scan-frame-corner {
        position: absolute;
        width: 20px;
        height: 20px;
        border-color: #667eea;
        border-style: solid;
        border-width: 0;
      }

      .tl {
        top: 0; left: 0;
        border-top-width: 3px;
        border-left-width: 3px;
        border-top-left-radius: 4px;
      }

      .tr {
        top: 0; right: 0;
        border-top-width: 3px;
        border-right-width: 3px;
        border-top-right-radius: 4px;
      }

      .bl {
        bottom: 0; left: 0;
        border-bottom-width: 3px;
        border-left-width: 3px;
        border-bottom-left-radius: 4px;
      }

      .br {
        bottom: 0; right: 0;
        border-bottom-width: 3px;
        border-right-width: 3px;
        border-bottom-right-radius: 4px;
      }

      .scan-line {
        position: absolute;
        top: 0;
        left: 10px;
        right: 10px;
        height: 2px;
        background: linear-gradient(90deg, transparent, #667eea, transparent);
        animation: scan 2s linear infinite;
      }
    }
  }

  .scan-placeholder {
    padding: 40px 20px;

    .placeholder-icon {
      font-size: 64px;
      margin-bottom: 16px;
    }

    .placeholder-text {
      font-size: 14px;
      color: #999;
      margin-bottom: 20px;
    }

    .start-scan-btn {
      padding: 16px 48px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 12px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    }

    .divider {
      font-size: 14px;
      color: #999;
      margin: 20px 0;
      position: relative;
    }

    .select-image-btn {
      padding: 12px 32px;
      background: white;
      color: #667eea;
      border: 2px solid #667eea;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s;
    }

    .select-image-btn:hover,
    .select-image-btn:active {
      background: #667eea;
      color: white;
    }
  }

  .scan-hint {
    font-size: 14px;
    color: #999;
    margin: 10px 0;
  }

  .scan-controls {
    margin-top: 15px;
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;

    .stop-scan-btn {
      padding: 10px 24px;
      background: #ff4757;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }

    .select-image-btn-inline {
      padding: 10px 20px;
      background: white;
      color: #667eea;
      border: 2px solid #667eea;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }
  }
}

@keyframes scan {
  0% { top: 10px; }
  100% { top: calc(100% - 10px); }
}

.manual-input {
  background: white;
  margin: 12px;
  padding: 20px;
  border-radius: 12px;

  .input-label {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #333;
  }

  .input-box {
    display: flex;
    gap: 12px;

    .token-input {
      flex: 1;
      padding: 12px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      font-size: 14px;
      outline: none;

      &:focus {
        border-color: #667eea;
      }
    }

    .submit-btn {
      padding: 12px 24px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }
  }
}

.scan-history {
  background: white;
  margin: 12px;
  padding: 20px;
  border-radius: 12px;

  .history-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #333;
  }

  .empty-history {
    text-align: center;
    padding: 40px 20px;
    color: #999;
    font-size: 14px;
  }

  .history-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;

    &:last-child {
      border-bottom: none;
    }

    .history-info {
      flex: 1;

      .history-name {
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 4px;
        color: #333;
      }

      .history-date {
        font-size: 12px;
        color: #999;
      }
    }

    .history-arrow {
      font-size: 18px;
      color: #999;
    }
  }
}
</style>
